import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { constants as imagesConstants } from '../../../../../../redux/images';
import { constants as workflowConstants } from '../../../../../../redux/workflows';
import InstructionModal from './InstructionModal';

const { FIRST_VIN, SECOND_VIN } = imagesConstants.IMAGE_SUB_TYPES;
const { INSTRUCTION_TYPE, INSTRUCTION_TYPE_NAMES } = imagesConstants;
const { CAR, TRUCK } = workflowConstants.VEHICLE_TYPES;

const VinInstructionSettings = ({ imageSubTypes, setFieldValue, vehicleType }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingSubType, setEditingSubType] = useState(null);
  const [currentTriggerRef, setCurrentTriggerRef] = useState(null);
  const firstEditButtonRef = useRef(null);
  const secondEditButtonRef = useRef(null);

  const firstVinSubType = imageSubTypes.find((st) => st.imageSubType === FIRST_VIN);
  const secondVinSubType = imageSubTypes.find((st) => st.imageSubType === SECOND_VIN);

  const showInstructions = vehicleType === CAR || vehicleType === TRUCK;

  // Define the display order to match the modal
  const getDisplayOrder = () => {
    if (vehicleType === 1) { // CAR
      return [
        INSTRUCTION_TYPE.FIRST_WINDSHIELD,
        INSTRUCTION_TYPE.DOOR_JAMB,
        INSTRUCTION_TYPE.UNDER_DRIVER_SEAT,
        INSTRUCTION_TYPE.PASSENGER_DOOR_AND_SEAT,
        INSTRUCTION_TYPE.ENGINE_ROOM,
        INSTRUCTION_TYPE.PASSENGER_SIDE
      ];
    }
    if (vehicleType === 3) { // TRUCK
      return [INSTRUCTION_TYPE.FIRST_WINDSHIELD, INSTRUCTION_TYPE.SECOND_WINDSHIELD];
    }
    return [];
  };

  const handleEdit = (subType, buttonRef) => {
    setEditingSubType(subType);
    setCurrentTriggerRef(buttonRef);
    setShowModal(true);
  };

  const handleSave = (instructions) => {
    const updatedImageSubTypes = imageSubTypes.map((st) => (st.imageSubType === editingSubType.imageSubType
      ? { ...st, instructions }
      : st));
    setFieldValue('imageSubTypes', updatedImageSubTypes);
  };

  const handleAddSecondImage = () => {
    const newImageSubTypes = [
      ...imageSubTypes,
      { imageSubType: SECOND_VIN, instructions: [] }
    ].sort((a, b) => a.imageSubType - b.imageSubType);
    setFieldValue('imageSubTypes', newImageSubTypes);
  };

  const handleRemoveSecondImage = () => {
    setFieldValue(
      'imageSubTypes',
      imageSubTypes.filter((st) => st.imageSubType !== SECOND_VIN)
    );
  };

  const getInstructionLabels = (instructions) => {
    if (instructions.length === 0) return '';

    // Sort instructions by display order
    const displayOrder = getDisplayOrder();
    const sortedInstructions = instructions
      .filter((inst) => displayOrder.includes(inst))
      .sort((a, b) => displayOrder.indexOf(a) - displayOrder.indexOf(b));

    return sortedInstructions
      .map((inst) => INSTRUCTION_TYPE_NAMES[inst])
      .join(', ');
  };

  return (
    <div className="vin-instruction-settings">
      {firstVinSubType && (
        <div className="instruction-item mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="font-weight-bold">First image</span>
            {
              showInstructions && (
                <button
                  ref={firstEditButtonRef}
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => handleEdit(firstVinSubType, firstEditButtonRef)}
                >
                  Edit
                </button>
              )
            }
          </div>
          {
            showInstructions && (
              <div className="text-muted">
                {getInstructionLabels(firstVinSubType.instructions) || 'No instructions selected'}
              </div>
            )
          }
        </div>
      )}

      {secondVinSubType && (
        <div className="instruction-item mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="font-weight-bold">Second image</span>
            <div>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mr-2 text-danger"
                onClick={handleRemoveSecondImage}
              >
                Remove
              </button>
              {
                showInstructions && (
                  <button
                    ref={secondEditButtonRef}
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => handleEdit(secondVinSubType, secondEditButtonRef)}
                  >
                    Edit
                  </button>
                )
              }
            </div>
          </div>
          {
            showInstructions && (
              <div className="text-muted">
                {getInstructionLabels(secondVinSubType.instructions) || 'No instructions selected'}
              </div>
            )
          }
        </div>
      )}

      {!secondVinSubType && vehicleType === 1 && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={handleAddSecondImage}
        >
          + Add second image
        </button>
      )}

      {editingSubType && (
        <InstructionModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingSubType(null);
            setCurrentTriggerRef(null);
          }}
          onSave={handleSave}
          selectedInstructions={editingSubType.instructions}
          vehicleType={vehicleType}
          triggerRef={currentTriggerRef}
        />
      )}
    </div>
  );
};

VinInstructionSettings.propTypes = {
  imageSubTypes: PropTypes.array.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  vehicleType: PropTypes.number.isRequired
};

export default VinInstructionSettings;
